import { Icon } from '@brandbank/portal-components';
import { Alert, Button, Skeleton, Stack, Tooltip } from '@mui/material';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { formatDate } from '@brandbank/portal-components';
import { asDate } from 'utils/string';
import getFeedCoverageFile from '../api/getFeedCoverageFile';
import useLatestFeedCoverageInfo from '../api/useLatestFeedCoverageInfo';
import { CoverageFileType, FeedCoverageInfo } from '../feeds.types';
import useUser from 'hooks/useUser';

const forbiddenToDownloadMessage =
  'You do not have permission to download coverage files for this feed.' as const;

const getErrorMessage = (status: number | undefined): string => {
  switch (status) {
    case 404:
      return 'Coverage file missing for this feed.';
    case 403:
      return forbiddenToDownloadMessage;
    default:
      return 'Unable to download coverage file.';
  }
};

type DownloadFeedCoverageButtonsProps = {
  subcode: string;
};

const DownloadFeedCoverageButtons = ({
  subcode,
}: DownloadFeedCoverageButtonsProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { id = '' } = useParams();

  const { hasRole } = useUser();

  const { data: coverageInfo, status: coverageReqStatus } =
    useLatestFeedCoverageInfo(id);

  const [covFileIsLoading, setCovFileIsLoading] = useState(false);

  const handleGetCoverageFile = async (
    fileName: string,
    coverageType: CoverageFileType
  ) => {
    setCovFileIsLoading(true);
    await getFeedCoverageFile(fileName, id, coverageType).catch(
      (e: AxiosError) => {
        const message = getErrorMessage(e?.response?.status);

        enqueueSnackbar(message, { variant: 'error' });
      }
    );
    setCovFileIsLoading(false);
  };

  const hasSubcodeSpecificCoverageRole = hasRole(
    'Download Feed Coverage',
    subcode
  );

  const renderCoverageFileDownload = (
    coverageItem: FeedCoverageInfo,
    coverageType: CoverageFileType
  ) => {
    const label =
      coverageType === 'latest-unsuccessful'
        ? 'Latest Unsuccessful Coverage File'
        : 'Latest Successful Coverage File';

    const tooltipTitle = !hasSubcodeSpecificCoverageRole
      ? forbiddenToDownloadMessage
      : '';

    // INTD-18190:UTC — keep processedDate rendering path in place for upcoming UTC test coverage
    const btnText = coverageItem.processedDate
      ? `${label} (${formatDate(coverageItem.processedDate, true, true)} (UTC))`
      : label;

    return (
      <Tooltip
        title={tooltipTitle}
        placement='bottom'
        PopperProps={{ sx: { textAlign: 'center' } }}
      >
        <span>
          <Button
            variant='outlined'
            color='primary'
            endIcon={<Icon iconName='downloadFile' />}
            onClick={() =>
              handleGetCoverageFile(coverageItem.fileName, coverageType)
            }
            disabled={!hasSubcodeSpecificCoverageRole || covFileIsLoading}
            fullWidth
          >
            {btnText}
          </Button>
        </span>
      </Tooltip>
    );
  };

  return (
    <Stack sx={{ pt: '1rem' }} spacing={0.5}>
      {coverageReqStatus === 'success' &&
        (!!coverageInfo.latest || !!coverageInfo.latestSuccessful) && (
          <>
            {coverageInfo?.latest &&
            coverageInfo.latest.errorMessage.length > 0 ? (
              renderCoverageFileDownload(
                coverageInfo.latest,
                'latest-unsuccessful'
              )
            ) : (
              <></>
            )}

            {coverageInfo.latestSuccessful ? (
              renderCoverageFileDownload(
                coverageInfo.latestSuccessful,
                'latest-successful'
              )
            ) : (
              <></>
            )}
          </>
        )}

      {coverageReqStatus === 'error' && (
        <Alert
          variant='standard'
          color='error'
          icon={
            <Icon
              iconName='restricted'
              fontSize='small'
              color='error'
              sx={{ mr: '0.5rem' }}
            />
          }
        >
          Failed to fetch coverage file information
        </Alert>
      )}

      {coverageReqStatus === 'loading' && (
        <Skeleton height={45} variant='rectangular' />
      )}
    </Stack>
  );
};

export default DownloadFeedCoverageButtons;
